import { useState, useEffect } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'

const endAdornmentSx = {
  '& .MuiOutlinedInput-root': {
    flexWrap: 'nowrap',
    pr: '56px !important',
  },
  '& .MuiAutocomplete-input': {
    minWidth: 0,
    flex: '1 1 auto',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 9,
  },
}

const filterSelectSx = {
  width: 'fit-content',
  maxWidth: '100%',
  flex: '0 0 auto',
  flexShrink: 0,
  ...endAdornmentSx,
  '& .MuiInputLabel-root': {
    px: 0.5,
    bgcolor: '#fff',
  },
}

const multiSelectSx = {
  width: 'fit-content',
  maxWidth: { xs: '100%', md: 280 },
  flex: '0 1 auto',
  '& .MuiOutlinedInput-root': {
    flexWrap: 'wrap',
    minHeight: 40,
    alignItems: 'center',
    py: 0.25,
  },
  '& .MuiInputLabel-root': {
    px: 0.5,
    bgcolor: '#fff',
  },
}

const pillSelectSx = {
  width: 'fit-content',
  maxWidth: '100%',
  flex: '0 0 auto',
  flexShrink: 0,
  ...endAdornmentSx,
  '& .MuiOutlinedInput-root': {
    ...endAdornmentSx['& .MuiOutlinedInput-root'],
    borderRadius: '20px',
    fontSize: '11.5px',
    bgcolor: '#fff',
    width: 'fit-content',
    minWidth: '100%',
  },
  '& .MuiAutocomplete-input': {
    minWidth: '2ch',
    width: 'auto !important',
    flex: '0 1 auto',
    textOverflow: 'clip',
    overflow: 'visible',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputBase-input::placeholder': {
    opacity: 1,
    color: '#555550',
  },
}

function computeSelectWidth(text, { pill = false, hasClear = false, multiple = false } = {}) {
  if (multiple) return 220
  const charWidth = pill ? 7 : 8
  const basePadding = 56
  const clearExtra = hasClear ? 24 : 0
  const minFloor = pill ? 96 : 200
  const maxCap = pill ? 320 : 320

  return Math.min(
    Math.max(String(text || '').length * charWidth + basePadding + clearExtra, minFloor),
    maxCap,
  )
}

const parseValueList = (value) => [
  ...new Set(
    String(value || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean),
  ),
]

const dedupeOptionsById = (options = []) => {
  const seen = new Set()
  return options.filter((option) => {
    const id = String(option?.id ?? '')
    if (!id) return true
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const dedupeSelectedOptions = (options = []) => {
  const seen = new Set()
  return options.filter((option) => {
    const id = String(option?.id ?? '')
    if (!id || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const DashboardSearchableSelect = ({
  label,
  options = [],
  value,
  onChange,
  loading = false,
  placeholder,
  size = 'small',
  sx,
  onSearch,
  pill = false,
  fullWidth = false,
  multiple = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const selectableOptions = multiple
    ? dedupeOptionsById(options.filter((option) => String(option.id ?? '') !== ''))
    : options

  const selectedIds = multiple ? parseValueList(value) : []
  const selected = multiple
    ? dedupeSelectedOptions(
        selectedIds
          .map((selectedId) =>
            selectableOptions.find(
              (option) => String(option.id) === String(selectedId),
            ),
          )
          .filter(Boolean),
      )
    : options.find((o) => String(o.id) === String(value?.id ?? value)) ||
      (value
        ? typeof value === 'object'
          ? value
          : { id: value, label: String(value) }
        : null) ||
      null

  const selectedLabel = multiple
    ? selected.map((option) => option.label).join(', ')
    : selected?.label || ''
  const fallbackLabel = placeholder || (pill ? label : '') || ''
  const displayLabel = selectedLabel || fallbackLabel
  const widthLabel = [selectedLabel, fallbackLabel, label].reduce(
    (longest, current) => (String(current || '').length > longest.length ? String(current || '') : longest),
    '',
  )
  const hasClear = multiple
    ? selected.length > 0
    : selected != null && String(selected.id ?? '') !== ''
  const computedMinWidth = computeSelectWidth(widthLabel, { pill, hasClear: hasClear && !pill, multiple })

  useEffect(() => {
    if (multiple) return
    setInputValue(selected?.label || '')
  }, [multiple, selected?.id, selected?.label])

  return (
    <Autocomplete
      multiple={multiple}
      size={size}
      disableCloseOnSelect={multiple}
      filterSelectedOptions={multiple}
      sx={{
        width: fullWidth ? '100%' : 'fit-content',
        minWidth: fullWidth ? 0 : computedMinWidth,
        maxWidth: '100%',
        ...(multiple ? multiSelectSx : pill ? pillSelectSx : filterSelectSx),
        ...sx,
      }}
      options={selectableOptions}
      filterOptions={onSearch ? (options) => options : undefined}
      value={multiple ? selected : selected}
      inputValue={inputValue}
      loading={loading}
      onChange={(_, next, reason) => {
        if (multiple) {
          onChange(dedupeSelectedOptions(next || []))
          if (reason === 'selectOption') {
            setInputValue('')
            onSearch?.('')
          }
          return
        }
        onChange(next)
        setInputValue(next?.label || '')
        if (next) onSearch?.('')
      }}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') {
          setInputValue(nextInput)
          onSearch?.(nextInput)
          return
        }
        if (reason === 'clear') {
          setInputValue('')
          onSearch?.('')
          return
        }
        if (multiple) {
          // Ignore reset while async option lists refresh (partner shop search).
          return
        }
        if (reason === 'reset') {
          setInputValue(nextInput)
        }
      }}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
      noOptionsText="No results"
      renderInput={(params) => (
        <TextField
          {...params}
          label={pill ? undefined : label}
          placeholder={
            multiple
              ? selected.length
                ? undefined
                : placeholder || `Select ${label}`
              : placeholder || (pill ? label : undefined)
          }
          title={displayLabel}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            title: displayLabel,
          }}
        />
      )}
    />
  )
}

export default DashboardSearchableSelect
